import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DatePicker, Input, Select } from "antd";
import DrawerSelect from "./DrawerSelect";
import { SimpleCross } from "../../assets/icons";
import ButtonLoader from "../../atoms/buttonLoader";
import { createExpense, getProjectsData, getVendorData } from "../../services/service";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { CustomToast } from "../../atoms/toastMessage";

const { TextArea } = Input;

const CreateExpenseSideBar = ({ visible, onClose, onSuccess, defaultProjectId = "", defaultVendorId = "" }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [fileError, setFileError] = useState(null);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!visible) return;
    (async () => {
      try {
        const [projRes, vendRes] = await Promise.all([
          getProjectsData(undefined, 1, 100, ""),
          getVendorData("", 1, 100, "", ""),
        ]);
        if (projRes?.success) setProjects(projRes?.data || projRes?.projects || []);
        if (vendRes?.success) setVendors(vendRes?.data || vendRes?.vendors || []);
      } catch (e) {
        // silent
      }
    })();
  }, [visible]);

  const sanitizeAmountInput = (raw) => {
    if (typeof raw !== 'string') raw = String(raw ?? '');
    // Remove everything except digits and dots
    let cleaned = raw.replace(/[^0-9.]/g, '');
    // Keep only the first dot
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }
    return cleaned;
  };

  const calculateDerivedValues = (grossTotal, taxRate) => {
    const gross = parseFloat(grossTotal) || 0;
    const rate = Number(taxRate) || 13;
    const divisor = 1 + rate / 100;
    const baseAmount = gross / divisor;
    const taxAmount = Math.max(0, gross - baseAmount);
    return {
      baseAmount: baseAmount.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      grossTotal: gross > 0 ? gross.toFixed(2) : "",
    };
  };

  const ErrorSpacer = ({ show, message }) => (
    <div style={{ minHeight: 14 }}>
      {show ? <p className="text-xs text-red-600">{message}</p> : null}
    </div>
  );

  const validateFile = (file) => {
    if (!file) return null;

    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    if (!allowedTypes.includes(file.type)) {
      return 'Only image files (JPG, PNG, GIF, WebP) and PDF files are allowed';
    }

    return null;
  };


  const formik = useFormik({
    initialValues: {
      title: "",
      project_id: defaultProjectId || "",
      amount: "", // derived base taxable amount
      tax: "", // derived tax amount
      total: "", // user-entered gross total
      tax_rate: 13, // selected tax rate (13 or 8)
      invoice_date: null,
      vendor_id: defaultVendorId || "",
      vendor_name: "",
      description: "",
      imgurl: null,
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      project_id: Yup.string().required("Project is required"),
      amount: Yup.number().typeError("Amount must be a number").positive("Amount must be positive").required("Amount is required"),
      tax: Yup.number().typeError("Tax must be a number").min(0, "Tax must be non-negative"),
      total: Yup.number().typeError("Total must be a number").positive("Total must be positive").required("Total is required"),
      invoice_date: Yup.mixed().required("Invoice date is required"),
      description: Yup.string().required("Description is required"),
      imgurl: Yup.mixed().required("Invoice document is required."),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      try {
        const fd = new FormData();
        // Align keys with backend: input_amount (base), tax_amount (calc), amount (gross total)
        fd.append("title", values.title);
        fd.append("project_id", values.project_id);
        {
          const grossTotalInput = sanitizeAmountInput(values.total);
          const taxRateVal = Number(values.tax_rate) || 13;
          const { baseAmount, taxAmount, grossTotal } = calculateDerivedValues(grossTotalInput, taxRateVal);
          fd.append("input_amount", baseAmount);
          fd.append("tax_amount", taxAmount);
          fd.append("amount", grossTotal);
          fd.append("tax_rate", taxRateVal);
        }
        if (values.invoice_date) {
          const d = dayjs(values.invoice_date).format("YYYY-MM-DD");
          fd.append("invoice_date", d);
        }
        if (values.vendor_id) fd.append("vendor_id", values.vendor_id);
        if (values.vendor_name) fd.append("vendor_name", values.vendor_name);
        fd.append("description", values.description);
        if (values.imgurl) {
          // Backend expects file field named 'imgurl'
          fd.append("imgurl", values.imgurl);
        }
        // Identity fields (match listing filters and worker pattern)
        if (user?.user?.id) {
          fd.append("user_id", user.user.id);
          fd.append("user_type", "manager");
          fd.append("manager_id", user.user.id);
        }

        const res = await createExpense(fd);
        if (res?.success) {
          resetForm();
          onSuccess?.();
          onClose();
          CustomToast({ type: 'success', message: res?.message || 'Expense created successfully' });
        } else {
          CustomToast({ type: 'error', message: res?.message || 'Failed to create expense' });
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Something went wrong while creating expense';
        CustomToast({ type: 'error', message: msg });
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik.values.project_id && projects?.length > 0) {
      const proj = projects?.find((p) => String(p?.id) === String(formik.values.project_id));
      if (proj && (proj.tax_rate || proj.default_tax_rate)) {
        const rate = Number(proj.tax_rate || proj.default_tax_rate);
        if (rate === 8 || rate === 13) {
          formik.setFieldValue('tax_rate', rate);
          if (formik.values.total) {
            const { baseAmount, taxAmount } = calculateDerivedValues(formik.values.total, rate);
            formik.setFieldValue("amount", baseAmount);
            formik.setFieldValue("tax", taxAmount);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.project_id, projects]);

  useEffect(() => {
    if (visible) {
      if (defaultProjectId) {
        formik.setFieldValue('project_id', defaultProjectId);
      }
      if (defaultVendorId) {
        formik.setFieldValue('vendor_id', defaultVendorId);
        const v = vendors?.find((x) => String(x?.id) === String(defaultVendorId));
        if (v) formik.setFieldValue('vendor_name', v?.title || v?.name || '');
      }
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, defaultProjectId, defaultVendorId]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30"></div>
      <div className="absolute top-0 right-0 w-[400px] h-full bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-4 flex-shrink-0">
          <h2 className="text-[20px] font-semibold text-[#1E3A8A]">Add Expense</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            {SimpleCross}
          </button>
          </div>
          <form onSubmit={formik.handleSubmit} className="flex-1 p-3 -mt-5 flex flex-col gap-2 overflow-y-auto" style={{ minHeight: 0 }}>
          <div>
            <Input
              value={formik.values.title}
              onChange={(e) => formik.setFieldValue("title", e.target.value)}
              placeholder="Title"
              className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.title && formik.errors.title ? "border-red-500" : "border-gray-300"}`}
            />
            <ErrorSpacer show={formik.touched.title && !!formik.errors.title} message={formik.errors.title} />
          </div>
          <div>
            <DrawerSelect
              value={formik.values.project_id}
              onChange={(val) => formik.setFieldValue("project_id", val)}
              options={projects?.map((p) => ({ value: p?.id, label: p?.title || p?.project || p?.name }))}
              placeholder="Select Project"
              className="w-full"
              searchable
            />
            <ErrorSpacer show={formik.touched.project_id && !!formik.errors.project_id} message={formik.errors.project_id} />
          </div>

          <div>
            <Input
              value={formik.values.total}
              onChange={(e) => {
                const val = sanitizeAmountInput(e.target.value);
                formik.setFieldValue("total", val);
                const { baseAmount, taxAmount } = calculateDerivedValues(val, formik.values.tax_rate);
                formik.setFieldValue("amount", baseAmount);
                formik.setFieldValue("tax", taxAmount);
              }}
              inputMode="decimal"
              pattern="[0-9]*[.]?[0-9]*"
              placeholder="Total ($)"
              className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.total && formik.errors.total ? "border-red-500" : "border-gray-300"}`}
            />
            <ErrorSpacer show={formik.touched.total && !!formik.errors.total} message={formik.errors.total} />
          </div>

          <div>
            <Input
              value={formik.values.amount}
              readOnly
              disabled
              placeholder="Amount (Taxable)"
              className="w-full h-[52px] px-3 border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <ErrorSpacer show={formik.touched.amount && !!formik.errors.amount} message={formik.errors.amount} />
          </div>

          <div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  value={formik.values.tax}
                  readOnly
                  disabled
                  placeholder="Tax Amount"
                  className="w-full h-[52px] px-3 border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div className="w-[100px]">
                <Select
                  value={formik.values.tax_rate}
                  onChange={(val) => {
                    formik.setFieldValue("tax_rate", val);
                    const { baseAmount, taxAmount } = calculateDerivedValues(formik.values.total, val);
                    formik.setFieldValue("amount", baseAmount);
                    formik.setFieldValue("tax", taxAmount);
                  }}
                  options={[
                    { value: 13, label: "13%" },
                    { value: 8, label: "8%" },
                  ]}
                  className="w-full h-[52px]"
                  style={{ height: 52 }}
                />
              </div>
            </div>
            <ErrorSpacer show={formik.touched.tax && !!formik.errors.tax} message={formik.errors.tax} />
          </div>

          <div>
            <DatePicker
              className="w-full h-[52px] rounded"
              value={formik.values.invoice_date}
              onChange={(d) => formik.setFieldValue("invoice_date", d)}
              format="YYYY-MM-DD"
              getPopupContainer={(trigger) => trigger.parentNode}
              popupStyle={{ zIndex: 999999 }}
              size="large"
              style={{ borderRadius: 4 }}
              placeholder="Select invoice date"
            />
            <ErrorSpacer show={formik.touched.invoice_date && !!formik.errors.invoice_date} message={formik.errors.invoice_date} />
          </div>

          <div>
            <DrawerSelect
              value={formik.values.vendor_id}
              onChange={(val) => {
                formik.setFieldValue("vendor_id", val);
                const v = vendors?.find((x) => String(x?.id) === String(val));
                formik.setFieldValue("vendor_name", v?.title || v?.name || "");
              }}
              options={vendors?.map((v) => ({ value: v?.id, label: v?.title || v?.name }))}
              placeholder="Vendor"
              className="w-full"
              searchable
            />
            <ErrorSpacer show={formik.touched.vendor_id && !!formik.errors.vendor_id} message={formik.errors.vendor_id} />
          </div>

          <div>
            <TextArea
              rows={3}
              value={formik.values.description}
              onChange={(e) => formik.setFieldValue("description", e.target.value)}
              placeholder="Description"
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.description && formik.errors.description ? "border-red-500" : "border-gray-300"}`}
            />
            <ErrorSpacer show={formik.touched.description && !!formik.errors.description} message={formik.errors.description} />
          </div>

          <div>
            <p className="font-normal text-[14px] text-[#4B5563] mb-2">Invoice Document (Image or PDF)</p>
            <div className="flex flex-col gap-2">
              <div className="relative">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    const file = e.currentTarget.files?.[0] || null;
                    const error = validateFile(file);
                    setFileError(error);
                    formik.setFieldValue("imgurl", file);
                  }}
                  className="w-full h-[64px] p-3 border rounded focus:outline-none border-[#E9E9E9] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#1E3A8A] file:text-white hover:file:bg-[#1E3A8A]/90"
                />
                {formik.values.imgurl && !fileError && (
                  <button
                    type="button"
                    onClick={() => {
                      formik.setFieldValue("imgurl", null);
                      setFileError(null);
                      // Clear the file input
                      const fileInput = document.querySelector('input[type="file"]');
                      if (fileInput) fileInput.value = '';
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 p-1 bg-white rounded-full shadow-sm"
                    title="Remove file"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div>
                {fileError && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-red-700">{fileError}</span>
                  </div>
                )}
                {!fileError && formik.touched.imgurl && formik.errors.imgurl && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-red-700">{formik.errors.imgurl}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Supported formats: Images (JPG, PNG, GIF, WebP) and PDF files (Max size: 10MB)
              </p>
            </div>
          </div>

          </form>
          <div className="flex-shrink-0 bg-white border-t border-gray-200 p-3" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="flex items-center justify-between gap-3">
              <button
                type="submit"
                form={undefined}
                disabled={isLoading || fileError}
                onClick={formik.handleSubmit}
                className={`w-[192px] text-white py-2 px-3 rounded ${isLoading || fileError
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#F97316] hover:bg-orange-600'
                  }`}
              >
                {isLoading ? <ButtonLoader /> : 'Create Expense'}
              </button>
              <div className="ml-auto text-right">
                {(() => {
                  const net = parseFloat(formik.values.amount) || 0;
                  return (
                    <div className="text-sm text-gray-700">
                      <span className="font-medium text-gray-600 mr-2">Net Amount:</span>
                      <span className="text-base font-semibold">{net.toFixed(2)}</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateExpenseSideBar;
